import { cx } from "@shared/components/untitledui/util/cx"
import { useLayoutEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react"

interface Props {
  "aria-label": string
  value: string
  /**
   * The text, once the user is done with it — on blur and on Enter, and only when it changed
   * since the field took focus. `previousValue` is the text as it stood at that focus.
   */
  onCommit: (value: string, previousValue: string) => void
  /**
   * Every keystroke, for callers that persist as the user types rather than at the end. Gets the
   * same `previousValue` as {@link onCommit}, so a caller can tell a real edit from a round trip
   * back to where the text started.
   */
  onChange?: (value: string, previousValue: string) => void
  placeholder?: string
  isReadOnly?: boolean
  autoFocus?: boolean
  className?: string
  onFocus?: () => void
  onBlur?: () => void
  onClick?: () => void
  [dataAttribute: `data-${string}`]: string | undefined
}

/**
 * One line of plain text edited where it sits — a title, not a document. A contenteditable
 * rather than an `<input>` because the text has to wrap: the month grid sizes each row to the
 * titles in it, so a long title grows its cell instead of scrolling out of sight on one line.
 *
 * The text is written to the element imperatively and never rendered as children — React
 * replacing the text node under the caret would collapse the selection. While the field has
 * focus the element is the text; blur hands it back to `value`, so a store that updates
 * mid-edit never moves the caret and a rejected edit is put back on screen.
 *
 * Enter commits and leaves; Escape puts back the text the field was focused with.
 */
export function InlineTextField({
  "aria-label": ariaLabel,
  value,
  onCommit,
  onChange,
  placeholder,
  isReadOnly,
  autoFocus,
  className,
  onFocus,
  onBlur,
  onClick,
  ...dataAttributes
}: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [isEditing, setIsEditing] = useState(false)
  const focusedWith = useRef("")

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    if (!isEditing && el.textContent !== value) el.textContent = value
    markEmpty(el)
  }, [value, isEditing])

  useLayoutEffect(() => {
    if (autoFocus) ref.current?.focus()
  }, [])

  const handleFocus = () => {
    focusedWith.current = value
    setIsEditing(true)
    onFocus?.()
  }

  const handleInput = () => {
    const el = ref.current
    if (!el) return
    markEmpty(el)
    onChange?.(el.textContent ?? "", focusedWith.current)
  }

  const handleBlur = () => {
    const committed = ref.current?.textContent ?? ""
    setIsEditing(false)
    onBlur?.()
    if (committed !== focusedWith.current) onCommit(committed, focusedWith.current)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      event.currentTarget.blur()
      return
    }
    if (event.key === "Escape") {
      event.preventDefault()
      // Put the original back through the same channel the keystrokes went out on, so a caller
      // that persisted them is rolled back too. The blur that follows then finds nothing changed.
      event.currentTarget.textContent = focusedWith.current
      onChange?.(focusedWith.current, focusedWith.current)
      event.currentTarget.blur()
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault()
    // One line: a pasted paragraph joins up rather than turning the title into a block of text.
    const text = event.clipboardData.getData("text/plain").replace(/\s*[\r\n]+\s*/g, " ")
    // Deprecated, but the only insert that leaves the browser's own undo stack intact — a
    // Range insertion loses it, and there is no replacement API.
    document.execCommand("insertText", false, text)
  }

  return (
    <div
      {...dataAttributes}
      ref={ref}
      role="textbox"
      aria-label={ariaLabel}
      aria-multiline={false}
      aria-readonly={isReadOnly || undefined}
      // plaintext-only is what keeps this a plain-text field: the browser strips markup on
      // the way in, so nothing but characters can reach `textContent`.
      contentEditable={isReadOnly ? false : "plaintext-only"}
      suppressContentEditableWarning
      tabIndex={0}
      data-placeholder={placeholder}
      className={cx(
        "cursor-text whitespace-pre-wrap",
        // The prompt for an empty field. data-empty rather than :empty because clearing the
        // text leaves the browser's bogus <br> behind, which defeats :empty.
        "data-empty:before:pointer-events-none data-empty:before:text-black/30 data-empty:before:content-[attr(data-placeholder)]",
        className,
      )}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onClick={onClick}
    />
  )
}

const markEmpty = (el: HTMLElement) => el.toggleAttribute("data-empty", el.textContent === "")
